"use client";

import { useEffect } from "react";
import { Modal } from "@/components/modal/modal";
import { IconButton } from "@/components/icon-button";
import { priceSymbol } from "@/utils/price-count";
import CrossIcon from "@/assets/icons/cross";
import BagIcon from "@/assets/icons/bag";
import ChevronRightIcon from "@/assets/icons/chevron-right";
import ProductImage from "./ProductImage";

export interface BuyNowModalProps {
  isOpen: boolean;
  onClose: () => void;
  productName: string;
  productImage: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
  shipping: number;
  total: number;
  freeShippingMessage?: string;
  onProceedToCheckout: () => void;
}

export const BuyNowModal = ({
  isOpen,
  onClose,
  productName,
  productImage,
  quantity,
  unitPrice,
  subtotal,
  shipping,
  total,
  freeShippingMessage,
  onProceedToCheckout,
}: BuyNowModalProps) => {
  useEffect(() => {
    if (isOpen) {
      console.log("[BuyNowModal] productImage URL:", productImage || "(empty)");
    }
  }, [isOpen, productImage]);

  const handleProceed = () => {
    onProceedToCheckout();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="small">
      {/* Teal header */}
      <div className="bg-primary px-6 py-4 rounded-t-3xl xl:rounded-t-3xl flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex-shrink-0 w-10 h-10 rounded-full border-2 border-white flex items-center justify-center text-white">
            <BagIcon />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-white text-lg">Buy Now</h3>
            <p className="text-white/90 text-sm">Proceed to checkout</p>
          </div>
        </div>
        <IconButton
          rounded
          size="small"
          onClick={onClose}
          className="flex-shrink-0 text-white hover:bg-white/20"
        >
          <CrossIcon />
        </IconButton>
      </div>

      {/* Product card */}
      <div className="p-6 bg-white">
        <div className="flex gap-4 items-center p-4 bg-gray-50 rounded-xl">
          <div className="relative w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-white">
            <ProductImage
              src={productImage}
              alt={productName}
              className="object-cover w-full h-full"
            />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-gray-900 truncate">
              {productName}
            </p>
            <p className="text-gray-600 text-sm">Qty: {quantity}</p>
            <p className="text-primary font-semibold text-sm">
              {priceSymbol(unitPrice)} each
            </p>
          </div>
        </div>

        <div className="border-b border-gray-200 my-4" />

        {/* Cost summary */}
        <div className="space-y-2 text-gray-700">
          <div className="flex justify-between">
            <span>Subtotal</span>
            <span>{priceSymbol(subtotal)}</span>
          </div>
          <div className="flex justify-between">
            <span>Shipping</span>
            <span>{priceSymbol(shipping)}</span>
          </div>
          <div className="flex justify-between font-semibold text-base pt-2">
            <span>Total</span>
            <span className="text-primary">{priceSymbol(total)}</span>
          </div>
        </div>

        {freeShippingMessage && (
          <div className="mt-4 px-4 py-3 bg-amber-50 rounded-lg flex items-center gap-2 text-amber-800 text-sm">
            <span>{freeShippingMessage}</span>
          </div>
        )}

        <div className="flex gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 px-4 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleProceed}
            className="flex-1 py-3 px-4 bg-primary text-white rounded-lg font-medium hover:bg-cyan-600 transition-colors flex items-center justify-center gap-2"
          >
            Proceed to Checkout
            <ChevronRightIcon size={18} />
          </button>
        </div>

        <p className="text-center text-gray-500 text-xs mt-4 flex items-center justify-center gap-1">
          Secure checkout with multiple payment options
        </p>
      </div>
    </Modal>
  );
};
